import { createTheme } from '@mui/material/styles';
import base from './base';
import { augmentColor } from './base';
import { derivedColors } from './base';
import { getBrand } from './brands';
import mobile from './surfaces/mobile';
import dashboard from './surfaces/dashboard';

const surfaces = { mobile, dashboard };

const isPlainObject = (value) => value?.constructor === Object;

function deepMerge(target, source) {
  const merged = { ...target };
  Object.entries(source).forEach(([key, value]) => {
    merged[key] = isPlainObject(value) && isPlainObject(merged[key]) ? deepMerge(merged[key], value) : value;
  });
  return merged;
}

const CSS_VARIABLES = { colorSchemeSelector: 'data' };

// Resolve as cores de `derivedColors` em cada esquema. As cores nativas de
// origem saem de um tema-rascunho, já completadas pelo MUI (inclusive o padrão
// dele quando a marca não define a cor naquele esquema). A cor que a marca
// definiu com `{ main }` não é derivada: só passa pelo augmentColor, como as
// cores de produto da base.
function resolveDerivedColors(options) {
  const schemes = Object.entries(options.colorSchemes ?? {});
  const withoutDerived = (palette) =>
    Object.fromEntries(Object.entries(palette ?? {}).filter(([name]) => !(name in derivedColors)));
  const draft = createTheme({
    ...options,
    colorSchemes: Object.fromEntries(
      schemes.map(([scheme, { palette, ...rest }]) => [scheme, { ...rest, palette: withoutDerived(palette) }]),
    ),
    cssVariables: CSS_VARIABLES,
  });

  const colorSchemes = Object.fromEntries(
    schemes.map(([scheme, schemeOptions]) => {
      const palette = { ...schemeOptions.palette };
      Object.entries(derivedColors).forEach(([name, { from, derive }]) => {
        const source = draft.colorSchemes[scheme].palette[from];
        if (palette[name]) {
          palette[name] = augmentColor({ color: palette[name], name });
        } else if (derive) {
          palette[name] = augmentColor({ color: { main: derive(source.main, scheme) }, name });
        } else {
          const { main, light, dark, contrastText } = source;
          palette[name] = { main, light, dark, contrastText };
        }
      });
      return [scheme, { ...schemeOptions, palette }];
    }),
  );
  return { ...options, colorSchemes };
}

// MUI defaults < base < marca < superfície. As camadas são mescladas antes do
// createTheme porque, com CSS vars, os argumentos extras do createTheme entram
// depois que as variáveis já foram geradas (os colorSchemes deles não virariam vars).
export function createAppTheme({ brand, surface, compact = false }) {
  const surfaceOptions = surfaces[surface]?.({ compact }) ?? {};
  const options = resolveDerivedColors([base, getBrand(brand).themeOptions, surfaceOptions].reduce(deepMerge, {}));

  return createTheme({
    ...options,
    cssVariables: CSS_VARIABLES,
  });
}
